<script setup>
import { WdsBreadCrumbs } from '@wds/components';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links';
import { DEFAULT_SEO_META_DATA } from '@/config/events';
import { PROPERTY_TYPE_DEFAULT } from '@/config/property-types';
import { commonValues } from '~/config/common.js';

definePageMeta({
  middleware: ['feature-gate', 'validate-location'],
  featureGate: 'web_enableOGevents',
  validateLocation: {
    level: 'city',
    rootKind: 'internal',
    rootSlugKey: 't_OGEVENTSSLUG',
    apiKey: 'hostel',
  },
});

// ### COMPOSABLES
const {
  $url,
  $tracking,
} = useNuxtApp();
const {
  t,
  locale,
} = useI18n();
const route = useRoute();
const { useUrl } = useUrls();
const { isExtraSmallScreen } = useMq();
const { useImage } = useImages();
const { buildSeo } = generateSeo();
const {
  defineEventSchema,
  defineLocationSchema,
  defineFaqSchema,
} = generateSchemas();
const {
  buildEventDetailsModel,
  getEventPageUrlFromRawEvent,
} = useEventDetailsModel();

// ### COMPOSABLES - APIs
const {
  getTopPropertiesByCityId,
  getCountryTopCities,
} = useApiCities();
const { getCityLocationTranslatedById } = useApiTranslator();
const { getEventsByCityNameAndCountryName } = useApiEvents();
const { getEventsCityPageTags } = useApiSeo();

// ### STORES
const sessionStore = useSessionStore();
const testingStore = useTestingStore();

// ### EXPERIMENTS
const isEventsContinentPageEnabled = testingStore.getFeatureVariables('web_enableOGevents')?.continent_enabled || false;
const isEventsCountryPageEnabled = testingStore.getFeatureVariables('web_enableOGevents')?.country_enabled || false;

// ### CONSTANTS INITIALIZATION
const locationKey = 'eventscity';
const segmentTracking = SEGMENT_PAGE_DATA.eventsCity;
const urlParams = route?.params || null;
const actualPropertyTypeObj = PROPERTY_TYPE_DEFAULT;
const GRAB_THE_APP_LINKS = APPDOWNLOADBANNERLINKS.EVENTS.CITY;
const appDownloadTopBannerDetails = {
  page: 'events-city',
  linkPaths: APPDOWNLOADBANNERLINKS?.EVENTS?.CITY?.mobile,
  linkParams: 'search',
};

// ### VARIABLES INITIALIZATION
const locationState = useLocationData();
const locationObj = locationState.value?.location;
if (!locationObj?.id) {
  console.error('### Events city not found');
  throw createError({
    statusCode: 404,
    statusMessage: 'Events city not found',
  });
}

// Same nested shape as event-details `moreEventsInEventCity` so list items and
// `buildEventDetailsModel` get continent/country/city with url-friendly slugs.
const eventsCityPageLocationData = {
  continent: {
    id: locationObj?.continentId,
    name: locationObj?.continent,
    urlFriendlyName: locationObj?.urlFriendlyContinent,
  },
  country: {
    id: locationObj?.countryId,
    name: locationObj?.country,
    urlFriendlyName: locationObj?.urlFriendlyCountry,
  },
  city: {
    id: locationObj?.id,
    name: locationObj?.name,
    urlFriendlyName: locationObj?.urlFriendlyName,
  },
};

const locationEvents = (await getEventsByCityNameAndCountryName(urlParams?.city, urlParams?.country)) || {};

const locationEventsList
  = locationEvents?.events && Array.isArray(locationEvents?.events) ? locationEvents?.events : [];

const topPropertiesInLocation = await getTopPropertiesByCityId(actualPropertyTypeObj?.key, locationObj?.id);

// ### TOP CITIES IN CITY COUNTRY
const countryTopCities = (await getCountryTopCities(locationObj?.countryId)) || [];
const seenOtherCitySlugs = new Set();
const cityOtherLocations = [];

for (const topCity of countryTopCities) {
  if (topCity?.id === locationObj?.id || topCity?.urlFriendlyName === locationObj?.urlFriendlyName) {
    continue;
  }
  const urlFriendlyCitySlug = topCity?.urlFriendlyName;
  if (urlFriendlyCitySlug) {
    if (seenOtherCitySlugs.has(urlFriendlyCitySlug)) {
      continue;
    }
    seenOtherCitySlugs.add(urlFriendlyCitySlug);
  }

  const locationData = {
    propertyTypeSlug: t(actualPropertyTypeObj.slug),
    urlFriendlyContinent: topCity.urlFriendlyContinent,
    urlFriendlyCountry: topCity.urlFriendlyCountry,
    urlFriendlyCity: topCity.urlFriendlyName,
  };
  cityOtherLocations.push({
    id: topCity.id,
    name: topCity.name,
    label: topCity.name,
    title: topCity.name,
    url: useUrl.getCityPageUrl(locationData),
  });
}

// ### CITY HERO IMAGE
const locationHeroImageObj = {
  mobile: locationObj?.image?.mobile,
  tablet: locationObj?.image?.tablet,
  desktop: locationObj?.image?.desktop,
  heroImage: locationObj?.image?.heroImage,
};

// TODO: We use here propertyTypeSlug - we should change to rootSlug to be more agnostic
const countryPageUrl = useUrl.getCountryPageUrl({
  propertyTypeSlug: t(actualPropertyTypeObj?.slug),
  urlFriendlyContinent: locationObj?.urlFriendlyContinent,
  urlFriendlyCountry: locationObj?.urlFriendlyCountry,
});

const cityPageUrl = useUrl.getCityPageUrl({
  propertyTypeSlug: t(actualPropertyTypeObj?.slug),
  urlFriendlyContinent: locationObj?.urlFriendlyContinent,
  urlFriendlyCountry: locationObj?.urlFriendlyCountry,
  urlFriendlyCity: locationObj?.urlFriendlyName,
});

// TODO: We use here propertyTypeSlug - we should change to rootSlug to be more agnostic
const cityEventsPageUrl = useUrl.getCityPageUrl({
  propertyTypeSlug: t('t_OGEVENTSSLUG'),
  urlFriendlyContinent: locationObj?.urlFriendlyContinent,
  urlFriendlyCountry: locationObj?.urlFriendlyCountry,
  urlFriendlyCity: locationObj?.urlFriendlyName,
});

const urlTranslations = await getCityLocationTranslatedById(
  locationObj?.continentId,
  locationObj?.countryId,
  locationObj?.id,
);
const pageCanonicalUrl = cityEventsPageUrl;
const eventsCityHrefLang = useUrl.generateHrefLangForEventsCity(urlTranslations);

const transcodeInterpolate = new TranscodeInterpolate();

// ### SEO
const seoData = await getEventsCityPageTags(locationObj?.id);
const seoExtraData = {
  canonicalUrl: pageCanonicalUrl,
  ogImage: useImage.getImage(locationHeroImageObj?.desktop) || '',
};

const sanitisedLocationEvents = computed(() => {
  if (!Array.isArray(locationEventsList)) {
    return [];
  }
  return locationEventsList.filter((item) => item !== null && typeof item === 'object');
});

const cityEventsModels = computed(() => {
  const rootSlug = t('t_OGEVENTSSLUG');
  return sanitisedLocationEvents.value
    .map((rawEvent) => {
      const existingLocation = rawEvent?.location;
      const mergedLocation
        = existingLocation && typeof existingLocation === 'object'
          ? {
            ...existingLocation,
            continent: eventsCityPageLocationData.continent,
            country: eventsCityPageLocationData.country,
            city: eventsCityPageLocationData.city,
          }
          : { ...eventsCityPageLocationData };

      const enrichedEvent = {
        ...rawEvent,
        location: mergedLocation,
      };
      const pageUrl = getEventPageUrlFromRawEvent(enrichedEvent, rootSlug);
      return buildEventDetailsModel(enrichedEvent, pageUrl);
    })
    .filter((model) => model !== null);
});

const locationEventsIdList = computed(() => {
  return cityEventsModels.value?.map((model) => model.eventId) || [];
});

const cityEventsSchemaList = computed(() => {
  return cityEventsModels.value?.map((model) => model.schema) || [];
});

const totalEventsInLocation
  = locationEvents?.totalEventsCount || locationEvents?.eventsCount || sanitisedLocationEvents?.value?.length || 0;

const aboutTextData = computed(() => {
  const seoCopy = seoData?.copy || DEFAULT_SEO_META_DATA.aboutSection.city;
  const h2RegEx = /<h2>([\s\S]*?)<\/h2>/i;
  const h3RegEx = /<h3>([\s\S]*?)<\/h3>/i;
  const h2Title = seoCopy.match(h2RegEx);
  const h3Subtitle = seoCopy.match(h3RegEx);
  const mainContent = seoCopy.replace(h2RegEx, '').replace(h3RegEx, '');

  const aboutH2
    = h2Title?.[1]?.trim().replace('##LOCATIONNAME##', '<span>##LOCATIONNAME##</span>') || '';
  const aboutH3 = h3Subtitle?.[1]?.trim() || '';
  const aboutMainContent = mainContent?.trim() || '';

  const currentYear = new Date().getFullYear();
  const baseReplacements = {
    LOCATIONNAME: locationObj?.name,
    YEAR: currentYear,
  };

  const textReplacements = {
    ...baseReplacements,
    NUMBEROFEVENTSPERLOCATION: totalEventsInLocation,
    EVENTSCOUNT: totalEventsInLocation,
    PROPCOUNT: locationObj?.totalPropertiesAvailable || locationObj?.numberOfProperties,
    REVIEWCOUNT: locationObj?.numberOfReviews,
    REVIEWSCOUNT: locationObj?.numberOfReviews,
    COUNTRY: locationObj?.country,
    CHOSENCOUNTRY: locationObj?.country,
    COUNTRYSEARCHURL: `<a href="${countryPageUrl}" target="_parent" rel="noopener" class="text-link underline bold">`,
    ENDCOUNTRYSEARCHURL: '</a>',
    CHOSENCONTINENT: locationObj?.continent,
    CHOSENCITY: locationObj?.name,
    CITY: locationObj?.name,
    PROPERTYTYPE: t(actualPropertyTypeObj?.transcode_s),
    PROPERTYTYPES: t(actualPropertyTypeObj?.transcode),
    SERVERNAME: commonValues.HOSTELWORLD,
    ACTIVEFILTER: '',
  };

  return {
    title: transcodeInterpolate.interpolate(aboutH2, baseReplacements),
    subtitle: transcodeInterpolate.interpolate(aboutH3, baseReplacements),
    content: transcodeInterpolate.interpolate(aboutMainContent, textReplacements),
  };
});

const getShareUrl = computed(() => {
  return cityEventsPageUrl;
});

const getLocationTitle = computed(() => {
  return `${locationObj?.name}, ${locationObj?.country}`;
});

const getMessage = computed(() => {
  return t('t_OGEVENTSMESSAGE', { LOCATIONNAME: [locationObj?.name, locationObj?.country].join(', ') });
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isExtraSmallScreen) ? 2 : null;
});

const tradReplacements = {
  CHOSENCONTINENT: locationObj?.continent,
  CHOSENCOUNTRY: locationObj?.country,
  CHOSENCITY: locationObj?.name,
  CITY: locationObj?.name,
  COUNTRY: locationObj?.country,
  COUNTRYSEARCHURL: countryPageUrl,
  LOCATIONNAME: locationObj?.name,
  NUMBEROFEVENTSPERLOCATION: totalEventsInLocation,
  EVENTSCOUNT: totalEventsInLocation,
  PROPCOUNT: locationObj?.totalPropertiesAvailable || locationObj?.numberOfProperties,
  PROPERTYTYPE: t(actualPropertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(actualPropertyTypeObj?.transcode),
  REVIEWCOUNT: locationObj?.numberOfReviews,
  REVIEWSCOUNT: locationObj?.numberOfReviews,
  SERVERNAME: commonValues.HOSTELWORLD,
  YEAR: new Date().getFullYear(),
  ACTIVEFILTER: '',
};

function interpolateFaqEntry(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return transcodeInterpolate.interpolate(text, tradReplacements, ['##', '##']);
}

const interpolatedFaqContent = (() => {
  const raw = seoData?.faqContent;
  if (!raw || !Object.keys(raw).length) {
    return {};
  }
  const result = {};
  for (const key of Object.keys(raw)) {
    result[key] = interpolateFaqEntry(raw[key]);
  }
  return result;
})();

const seoMetaData = buildSeo.getMetaData(seoData, seoExtraData, tradReplacements, false, locationKey);

const getFaqContent = computed(() => {
  const faqContent = Object.keys(interpolatedFaqContent).length > 0 ? interpolatedFaqContent : {};
  const newFaqContent = [];

  if (Object.keys(faqContent).length > 0) {
    Object.keys(faqContent).forEach((faqKey) => {
      if (!faqKey.match(/ response$/) && !!faqContent[faqKey]) {
        const faqHeader = $url.processHtmlContent(faqContent[faqKey]);
        const faqResponse = $url.processHtmlContent(faqContent[`${faqKey} response`]);

        newFaqContent.push({
          header: faqHeader?.processedContent || faqContent[faqKey],
          content: faqResponse?.processedContent ?? faqContent[`${faqKey} response`] ?? '',
        });
      }
    });
  }

  return newFaqContent;
});

const hasFaq = computed(() => {
  return getFaqContent?.value?.length > 0;
});

function onFaqExpand(faqIndex) {
  $tracking?.onTrackEvent({
    action: 'FAQ Click',
    name: segmentTracking.NAME,
    occasion_genius_faq: faqIndex ?? '',
    page_type: segmentTracking.TYPE,
    occasion_genius_events: locationEventsIdList.value?.join(',') || '',
    occasion_genius_event_city: locationObj?.name,
    occasion_genius_event_country: locationObj?.country,
  });
}

const hasPopularLocations = computed(() => {
  return cityOtherLocations?.length > 0;
});

const crumbs = [
  {
    label: t('t_HOME'),
    title: t('t_HOME'),
    link: useUrl.getStaticBaseURL(),
  },
  {
    label: t('t_A11YOGEVENTS'),
    title: t('t_OGEVENTS'),
    link: useUrl.getEventsHomeUrl({ rootSlug: t('t_OGEVENTSSLUG') }),
  },
  ...(isEventsContinentPageEnabled
    ? [{
      label: t('t_A11YOGEVENTSLISTLOCATION', {
        LOCATIONNAME: locationObj?.continent,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      title: t('t_A11YBCTEXTLOCATION', {
        LOCATIONNAME: locationObj?.continent,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      link: useUrl.getEventsContinentPageUrl({
        rootSlug: t('t_OGEVENTSSLUG'),
        urlFriendlyContinent: locationObj?.urlFriendlyContinent,
      }),
    }]
    : []),
  ...(isEventsCountryPageEnabled
    ? [{
      label: t('t_A11YOGEVENTSLISTLOCATION', {
        LOCATIONNAME: locationObj?.country,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      title: t('t_A11YBCTEXTLOCATION', {
        LOCATIONNAME: locationObj?.country,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      link: useUrl.getEventsCountryPageUrl({
        rootSlug: t('t_OGEVENTSSLUG'),
        urlFriendlyContinent: locationObj?.urlFriendlyContinent,
        urlFriendlyCountry: locationObj?.urlFriendlyCountry,
      }),
    }]
    : []),
  {
    label: t('t_A11YOGEVENTSLISTLOCATION', {
      LOCATIONNAME: locationObj?.name,
      PROPERTYTYPE: t('t_OGEVENTS'),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: locationObj?.name,
      PROPERTYTYPE: t('t_OGEVENTS'),
    }),
    link: useUrl.getEventsCityPageUrl({
      rootSlug: t('t_OGEVENTSSLUG'),
      urlFriendlyContinent: locationObj?.urlFriendlyContinent,
      urlFriendlyCountry: locationObj?.urlFriendlyCountry,
      urlFriendlyCity: locationObj?.urlFriendlyName,
    }),
  },
];

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(eventsCityHrefLang),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### SCHEMAS
const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({
    '@id': '#hostelworldNavigation',
    'name': 'Breadcrumb',
    'itemListElement': schemaCrumbs,
  }),
  defineLocationSchema('City', locationObj, seoMetaData),
  defineEventSchema(cityEventsSchemaList?.value),
  defineFaqSchema(hasFaq.value ? getFaqContent?.value : null),
]);

// ### TRACKING
sessionStore.setEntryPoint(locationKey.toUpperCase());

const topPropertiesInLocationForTracking = topPropertiesInLocation?.map((property) => property.id) || [];

const commonTrackingInfo = {
  page_type: segmentTracking.TYPE,
  name: segmentTracking.NAME,
  occasion_genius_event_city: locationObj?.name,
  occasion_genius_event_country: locationObj?.country,
  top_30_properties: topPropertiesInLocationForTracking.join('|'),
  occasion_genius_events: locationEventsIdList.value?.join('|') || '',
};

function onShareOpen() {
  $tracking?.onTrackEvent({
    event_name: segmentTracking.EVENT,
    action: 'Share Link Clicked',
    ...commonTrackingInfo,
  });
}
function onShareMethodSelect(method) {
  $tracking?.onTrackEvent({
    event_name: segmentTracking.EVENT,
    action: 'Share Link Success',
    share_og_method: method,
    ...commonTrackingInfo,
  });
}

onMounted(() => {
  const trackingInfo = {
    view: {
      event_name: segmentTracking.NAME,
      ...commonTrackingInfo,
    },
    track: {
      event_name: segmentTracking.VIEWED,
      ...commonTrackingInfo,
    },
  };

  $tracking?.triggerPageView(trackingInfo.view);
  $tracking?.triggerPageEvent(trackingInfo.track);
});
</script>

<script>
/* eslint-disable */
import { ca } from '@/lib/channelAttribution.js';
if (import.meta.client) {
  ca?.setChannelAttribution(null, 'EVENTSCITY');
}
/* eslint-enable */
</script>

<template>
  <LazyCommonHwAppDownloadBanner v-bind="appDownloadTopBannerDetails" />

  <header class="hw-wrapper events-navbar">
    <CommonHwNavBar :show-language-pill="false" />
  </header>

  <section class="hw-wrapper events-hero-section">
    <EventsHwHeroTitle
      :title="t('t_OGEVENTSTOPTITLE')"
      :subtitle="getLocationTitle"
      :share-url="getShareUrl"
      :share-message="getMessage"
      :share-popup-heading="t('t_OGEVENTS')"
      @share-open="onShareOpen"
      @share-method-select="onShareMethodSelect"
    />
  </section>

  <section class="hw-wrapper events-about-section">
    <EventsHwEventsHeroImage :image="locationHeroImageObj" />
    <EventsHwEventsAboutLocation
      :location="locationObj"
      :text="aboutTextData"
    />
  </section>

  <section
    v-if="cityEventsModels?.length"
    class="hw-wrapper events-list-section"
  >
    <h2 class="events-things-to-do-heading">
      <i18n-t keypath="t_OGEVENTSBESTTHINGSTODOINLOCATION">
        <template #CITYNAME>
          <span>{{ locationObj?.name }}</span>
        </template>
      </i18n-t>
    </h2>
    <EventsHwEventsList :list="cityEventsModels" />
  </section>

  <section
    v-if="topPropertiesInLocation"
    class="hw-wrapper best-properties-list-section"
  >
    <!-- TODO: Replace empty properties with real data when events best-properties API/section is implemented -->
    <CommonHwTopFilterCards
      :properties="topPropertiesInLocation"
      :title="
        $t('t_BESTPROPTYPEBYFILTERINCITY', {
          PROPERTYTYPE: $t(actualPropertyTypeObj?.transcode).toLowerCase(),
          FILTERTEXT: '',
          CITYNAME: `<span>${locationObj?.name}</span>`,
        })
      "
      :view-more="true"
      :view-more-url="cityPageUrl"
    />
  </section>

  <section
    v-if="hasFaq || hasPopularLocations"
    class="hw-wrapper events-faq-and-popular-locations-section split-view"
  >
    <CommonHwFaq
      v-if="hasFaq"
      :content="getFaqContent"
      @expand="onFaqExpand"
    />
    <CommonHwOtherLocationsInLocation
      v-if="hasPopularLocations"
      :location-name="locationObj?.country"
      :other-locations="cityOtherLocations"
      :property-type-obj="actualPropertyTypeObj"
    />
  </section>

  <section class="hw-wrapper grab-the-app-section">
    <CommonHwGrabTheAppBanner :links="GRAB_THE_APP_LINKS" />
  </section>

  <section class="hw-wrapper breadcrumbs-section">
    <WdsBreadCrumbs
      :breadcrumbs="crumbs"
      :max-crumbs-shown="getCrumbsShownByViewport"
      :reversed="false"
    />
  </section>
</template>

<style scoped lang="scss">
.events-navbar {
  padding: 0;
}

.events-hero-section {
  margin-bottom: $wds-spacing-l;
}

.events-about-section {
  gap: $wds-spacing-l;
  display: grid;
  grid-template-columns: 1fr;

  @include tablet {
    grid-template-columns: 1fr 1fr;
  }
}

.events-list-section {
  margin-block: $wds-spacing-l;

  .events-things-to-do-heading {
    @include title-3-bld;

    margin-bottom: $wds-spacing-m;
    overflow-wrap: break-word;
    color: $wds-color-ink-darker;

    :deep(span) {
      color: $wds-color-purple;
    }

    @include tablet-large {
      @include title-2-bld;
    }
  }
}

.best-properties-list-section {
  margin-block: $wds-spacing-xl;
}

.events-faq-and-popular-locations-section {
  display: grid;
  grid-auto-flow: row;
  grid-auto-columns: 1fr;
  gap: wds-rem(72px);
  align-items: flex-start;

  @include desktop {
    &.split-view {
      gap: wds-rem(40px);
      grid-auto-flow: column;
    }
  }
}

.grab-the-app-section {
  margin-block: $wds-spacing-xxl;
}

.breadcrumbs-section {
  padding-left: $wds-spacing-s;

  @include tablet {
    padding-left: $wds-spacing-m;
  }

  @include desktop {
    padding-left: $wds-spacing-xl;
  }
}
</style>
